import { AnimatePresence, motion } from 'motion/react'
import { SlidersHorizontal, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'

import { categories, type Category } from '../../data/products'
import { ProductCard } from '../components/ProductCard'
import { Button, Container } from '../components/ui'
import { useCatalog } from '../lib/catalog'
import { cx } from '../lib/cx'
import { useI18n } from '../lib/i18n'

export function Shop() {
  const { t } = useI18n()
  const { products } = useCatalog()
  const [params, setParams] = useSearchParams()
  const [mobileOpen, setMobileOpen] = useState(false)
  const category = params.get('kategoriya') ?? ''
  const sort = params.get('tartib') === 'new' ? 'new' : 'featured'

  function update(next: Record<string, string>) {
    const merged = new URLSearchParams(params)
    merged.delete('narx')
    for (const [k, v] of Object.entries(next)) v ? merged.set(k, v) : merged.delete(k)
    setParams(merged, { replace: true })
  }

  const list = useMemo(() => {
    let items = products.filter((p) => !category || p.category === category)
    if (sort === 'new') items = [...items].sort((a, b) => (b.badge === 'New' ? 1 : 0) - (a.badge === 'New' ? 1 : 0))
    if (sort === 'featured') items = [...items].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0))
    return items
  }, [products, category, sort])

  const title = category ? t(`category_${category}`) : t('all_products')
  const activeCount = category ? 1 : 0
  const sortOptions = [
    { key: 'featured', label: t('featured') },
    { key: 'new', label: t('newest') },
  ]

  const filters = (
    <div className="flex flex-col gap-8">
      <FilterGroup label={t('category')}>
        {[{ key: '' as Category | '', label: t('all_categories') }, ...categories.map((item) => ({ ...item, label: t(`category_${item.key}`) }))].map((c) => (
          <FilterOption key={c.key} active={category === c.key} onClick={() => update({ kategoriya: c.key })}>
            {c.label}
            <span className="ml-auto text-[12px] tabular-nums text-faint">
              {c.key ? products.filter((p) => p.category === c.key).length : products.length}
            </span>
          </FilterOption>
        ))}
      </FilterGroup>
    </div>
  )

  return (
    <Container className="max-w-6xl px-4 pt-24 sm:px-6 sm:pt-28">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[12.5px] font-medium uppercase tracking-[0.16em] text-accent">{t('catalog')}</p>
          <h1 className="font-display mt-2 text-[40px] leading-none sm:text-[54px]">
            {title} <span className="align-middle text-[20px] text-faint">{list.length}</span>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" className="lg:hidden" onClick={() => setMobileOpen(true)}>
            <SlidersHorizontal />
            {t('filters')}{activeCount > 0 && ` (${activeCount})`}
          </Button>
          <label className="relative">
            <span className="sr-only">{t('sorting')}</span>
            <select
              value={sort}
              onChange={(e) => update({ tartib: e.target.value })}
              className="min-h-11 appearance-none rounded-[12px] border border-line-strong bg-surface/70 pl-4 pr-9 text-[13.5px] transition-colors focus:border-forest focus:outline-none"
            >
              {sortOptions.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
            <svg className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m6 9 6 6 6-6" />
            </svg>
          </label>
        </div>
      </div>

      <div className="mt-10 grid gap-10 lg:grid-cols-[224px_1fr] lg:gap-14">
        <aside className="hidden lg:block">
          <div className="sticky top-28">{filters}</div>
        </aside>
        <div>
          <motion.div layout className="grid grid-cols-2 gap-x-3 gap-y-8 sm:gap-x-5 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            <AnimatePresence mode="popLayout">
              {list.map((p, i) => (
                <motion.div key={p.slug} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
                  <ProductCard product={p} index={i} />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
          {list.length === 0 && (
            <div className="py-24 text-center">
              <p className="font-display text-[30px]">{t('no_products_category')}</p>
              <Button variant="secondary" className="mt-6" onClick={() => update({ kategoriya: '' })}>
                {t('clear_filters')}
              </Button>
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div className="fixed inset-0 z-50 bg-fg/35 lg:hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setMobileOpen(false)}>
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-x-2 bottom-2 max-h-[85vh] overflow-y-auto rounded-dialog bg-bg p-6 shadow-drawer"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between">
                <p className="font-display text-[26px]">{t('filters')}</p>
                <button type="button" aria-label={t('close')} onClick={() => setMobileOpen(false)} className="grid size-11 place-items-center rounded-control hover:bg-surface-2">
                  <X className="size-5" />
                </button>
              </div>
              <div className="mt-6">{filters}</div>
              <Button className="mt-8 w-full" onClick={() => setMobileOpen(false)}>
                {list.length} {t('show_products')}
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Container>
  )
}

function FilterGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[12px] font-medium uppercase tracking-[0.14em] text-muted">{label}</p>
      <ul className="mt-3 flex flex-col gap-0.5">{children}</ul>
    </div>
  )
}

function FilterOption({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        aria-pressed={active}
        className={cx(
          'flex min-h-11 w-full items-center gap-2 rounded-[11px] px-2.5 text-left text-[13.5px] transition-colors',
          active ? 'bg-forest/10 font-medium text-forest' : 'text-muted hover:text-fg',
        )}
      >
        {children}
      </button>
    </li>
  )
}
